import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Close, MoreVert } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { Layout } from '@roc/feature-app-core';
import { Button, CurrencyField, Paper } from '@roc/ui';
import React, { useEffect, useMemo, useState } from 'react';
import {
  AppraisalPaymentAmc,
  AppraisalPaymentLoanLookup,
  AppraisalPaymentLoanValidationError,
  AppraisalPaymentLoanValidationResponse,
  BulkAppraisalPaymentLinkResponse,
  BulkAppraisalPaymentsService,
} from './bulkAppraisalPaymentsService';

const RUSH_FEE = 600;

type ValuationOrder = {
  id: string;
  clientValuationReferenceId: number;
  address: string;
  amount: number;
  isRush: boolean;
};

type AppraisalPaymentLoan = AppraisalPaymentLoanLookup & {
  orders: ValuationOrder[];
};

const service = new BulkAppraisalPaymentsService();

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);

const getOrderTotal = (order: ValuationOrder) =>
  order.amount + (order.isRush ? RUSH_FEE : 0);

const getLoanTotal = (loan: AppraisalPaymentLoan) =>
  loan.orders.reduce((total, order) => total + getOrderTotal(order), 0);

const toPaymentLoanRequest = (loan: AppraisalPaymentLoan) => ({
  loanId: loan.loanId,
  orders: loan.orders.map(order => ({
    clientValuationReferenceId: order.clientValuationReferenceId,
    amount: order.amount,
    rush: order.isRush,
  })),
});

const toPaymentLoan = (loan: AppraisalPaymentLoanLookup): AppraisalPaymentLoan => {
  const valuationCount = loan.valuationCount || loan.clientValuationReferenceIds?.length || 1;
  const baseAmount = valuationCount > 0 ? (Number(loan.appraisalCost) || 0) / valuationCount : 0;
  const valuationOrders = loan.valuationOrders?.length
    ? loan.valuationOrders
    : (loan.clientValuationReferenceIds || []).map(clientValuationReferenceId => ({
      clientValuationReferenceId,
      amount: baseAmount,
      rush: false,
    }));

  return {
    ...loan,
    orders: valuationOrders.map(order => ({
      id: `${loan.loanId}-${order.clientValuationReferenceId}`,
      clientValuationReferenceId: order.clientValuationReferenceId,
      address: loan.propertyAddress || loan.dealName || `Loan ${loan.loanId}`,
      amount: Number(order.amount) || 0,
      isRush: Boolean(order.rush),
    })),
  };
};

const parseLoanIds = (value: string) =>
  value
    .split(/[,\s]+/)
    .map(item => item.trim())
    .filter(Boolean)
    .map(Number)
    .filter(Number.isFinite);

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    padding: theme.spacing(3),
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(4),
  },
  headerDot: {
    backgroundColor: '#2563EB',
    borderRadius: '50%',
    height: 8,
    width: 8,
  },
  fieldRow: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: '1fr',
    marginBottom: theme.spacing(2),
  },
  loanEntryRow: {
    alignItems: 'stretch',
    display: 'grid',
    gap: theme.spacing(1.5),
    gridTemplateColumns: 'minmax(0, 1fr) 180px',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  addLoanButton: {
    height: 56,
    minWidth: 180,
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  loanInputHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
  },
  loanInputLabel: {
    color: '#374151',
    fontSize: 14,
    fontWeight: 500,
  },
  loanInputField: {
    '& .MuiOutlinedInput-root': {
      alignItems: 'center',
      minHeight: 56,
      paddingBottom: 6,
      paddingTop: 6,
    },
  },
  loanChip: {
    borderRadius: 6,
    fontSize: 14,
    height: 32,
    margin: theme.spacing(0.25, 0.5, 0.25, 0),
  },
  inputHint: {
    color: '#6B7280',
    fontSize: 12,
    textAlign: 'right',
  },
  validationErrors: {
    color: '#B91C1C',
    fontSize: 12,
    marginTop: theme.spacing(-1),
    marginBottom: theme.spacing(2),
  },
  optionText: {
    display: 'flex',
    flexDirection: 'column',
  },
  optionMeta: {
    color: '#6B7280',
    fontSize: 12,
  },
  tableWrap: {
    border: '1px solid #E5E7EB',
    borderRadius: 4,
    marginBottom: theme.spacing(4),
    overflow: 'hidden',
  },
  tableHead: {
    backgroundColor: '#F9FAFB',
  },
  tableHeaderCell: {
    color: '#374151',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0,
    textTransform: 'uppercase',
  },
  loanIdCell: {
    color: '#111827',
    fontWeight: 700,
  },
  amountCell: {
    fontWeight: 700,
  },
  totalRow: {
    backgroundColor: '#F9FAFB',
  },
  actions: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(2),
  },
  linkPanel: {
    backgroundColor: '#ECFDF5',
    border: '1px solid #BBF7D0',
    borderRadius: 4,
    padding: theme.spacing(2),
  },
  linkTitle: {
    color: '#047857',
    fontSize: 12,
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    textTransform: 'uppercase',
  },
  linkContent: {
    alignItems: 'center',
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: '1fr auto',
  },
  linkValue: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #D1FAE5',
    borderRadius: 4,
    color: '#2563EB',
    fontFamily: 'monospace',
    fontSize: 12,
    padding: theme.spacing(1.5),
    wordBreak: 'break-all',
  },
  dialogPaper: {
    '& .MuiDialog-paper': {
      borderRadius: 8,
      maxWidth: 960,
      width: 'calc(100% - 64px)',
    },
  },
  confirmDialogPaper: {
    '& .MuiDialog-paper': {
      borderRadius: 8,
      maxWidth: 400,
      width: 'calc(100% - 64px)',
    },
  },
  confirmDialogTitle: {
    padding: theme.spacing(3, 3, 1),
  },
  confirmDialogContent: {
    padding: theme.spacing(0, 3, 3),
  },
  confirmDialogText: {
    fontSize: '0.875rem',
    marginBottom: theme.spacing(3),
  },
  dialogTitle: {
    padding: theme.spacing(4, 4, 1),
  },
  dialogTitleRow: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  dialogContent: {
    padding: theme.spacing(0, 4, 4),
  },
  orderList: {
    border: '1px solid #D1D5DB',
    borderRadius: 8,
    margin: theme.spacing(3, 0),
    overflow: 'hidden',
  },
  orderRow: {
    alignItems: 'center',
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'minmax(240px, 1fr) 180px 160px 120px',
    minHeight: 86,
    padding: theme.spacing(2, 3),
    '&:not(:last-child)': {
      borderBottom: '1px solid #E5E7EB',
    },
  },
  rushRow: {
    backgroundColor: '#F1F5F9',
  },
  address: {
    color: '#111827',
    fontSize: 18,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rushCell: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  rushLabel: {
    color: '#111827',
    fontSize: 18,
    fontWeight: 500,
    lineHeight: 1.1,
  },
  rushFee: {
    color: '#6B7280',
    fontSize: 14,
  },
  lineTotal: {
    color: '#111827',
    fontSize: 18,
    fontWeight: 700,
    textAlign: 'right',
  },
  totalsPanel: {
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    marginBottom: theme.spacing(4),
    padding: theme.spacing(2, 3),
  },
  totalLine: {
    alignItems: 'center',
    color: '#374151',
    display: 'flex',
    fontSize: 18,
    justifyContent: 'space-between',
    padding: theme.spacing(1, 0),
  },
  finalTotalLine: {
    borderTop: '1px solid #E5E7EB',
    color: '#111827',
    fontSize: 22,
    fontWeight: 700,
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(2),
  },
  dialogActions: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
  },
}));

export const BulkAppraisalPayments = () => {
  const classes = useStyles();
  const [amcs, setAmcs] = useState<AppraisalPaymentAmc[]>([]);
  const [amcId, setAmcId] = useState<number | ''>('');
  const [loanInput, setLoanInput] = useState('');
  const [loanOptions, setLoanOptions] = useState<AppraisalPaymentLoan[]>([]);
  const [loans, setLoans] = useState<AppraisalPaymentLoan[]>([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [validationErrors, setValidationErrors] = useState<AppraisalPaymentLoanValidationError[]>([]);
  const [generatedLink, setGeneratedLink] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedLoanId, setSelectedLoanId] = useState<number | null>(null);
  const [editLoan, setEditLoan] = useState<AppraisalPaymentLoan | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;

    service.getAmcs()
      .then(response => {
        if (!isMounted) {
          return;
        }

        const nextAmcs = response.data.data || [];
        setAmcs(nextAmcs);
        const tamarisk = nextAmcs.find((amc: AppraisalPaymentAmc) => amc.name === 'Tamarisk');
        setAmcId(tamarisk?.amcId || nextAmcs[0]?.amcId || '');
      })
      .catch(() => {
        if (isMounted) {
          setAmcs([]);
          setValidationErrors([{ loanId: 0, reason: 'Unable to load AMCs. Please try again.' }]);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!amcId || loanInput.trim().length < 3) {
      setLoanOptions([]);
      setLookupLoading(false);
      return;
    }

    let isMounted = true;
    setLookupLoading(true);
    const timeout = window.setTimeout(() => {
      service
        .lookupLoans(Number(amcId), loanInput.trim())
        .then(response => {
          if (!isMounted) {
            return;
          }
          const lookupLoans = (response.data.data || []).map(toPaymentLoan);
          setLoanOptions(lookupLoans);
        })
        .catch(() => {
          if (isMounted) {
            setLoanOptions([]);
          }
        })
        .finally(() => {
          if (isMounted) {
            setLookupLoading(false);
          }
        });
    }, 400);

    return () => {
      isMounted = false;
      window.clearTimeout(timeout);
    };
  }, [amcId, loanInput]);

  const total = useMemo(
    () => loans.reduce((sum, loan) => sum + getLoanTotal(loan), 0),
    [loans]
  );

  const selectedLoan = loans.find(loan => loan.loanId === selectedLoanId);
  const editSubtotal =
    editLoan?.orders.reduce((sum, order) => sum + order.amount, 0) || 0;
  const editRushCount =
    editLoan?.orders.filter(order => order.isRush).length || 0;
  const editRushFees = editRushCount * RUSH_FEE;
  const editTotal = editSubtotal + editRushFees;

  const appendLoans = (nextLoans: AppraisalPaymentLoan[]) => {
    setLoans(currentLoans => {
      const selectedLoanIds = new Set(currentLoans.map(loan => loan.loanId));
      return [
        ...currentLoans,
        ...nextLoans.filter(loan => !selectedLoanIds.has(loan.loanId)),
      ];
    });
    setGeneratedLink('');
  };

  const handleAddLoan = () => {
    const loanIds = parseLoanIds(loanInput).filter(
      loanId => !loans.some(loan => loan.loanId === loanId)
    );

    if (!amcId || !loanIds.length) {
      return;
    }

    service
      .validateLoans(Number(amcId), loanIds)
      .then(response => {
        const data: AppraisalPaymentLoanValidationResponse = response.data.data;
        appendLoans((data.validLoans || []).map(toPaymentLoan));
        setValidationErrors(data.invalidLoans || []);
        setLoanInput('');
      })
      .catch(() => {
        setValidationErrors([
          {
            loanId: 0,
            reason: 'Unable to validate loan IDs',
          },
        ]);
      });
  };

  const handleRemoveLoan = (loanId?: number) => {
    if (!loanId) {
      return;
    }
    setLoans(currentLoans => currentLoans.filter(loan => loan.loanId !== loanId));
    setGeneratedLink('');
    handleCloseMenu();
  };

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    loanId: number
  ) => {
    setMenuAnchor(event.currentTarget);
    setSelectedLoanId(loanId);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setSelectedLoanId(null);
  };

  const handleEditDetails = () => {
    if (selectedLoan) {
      setEditLoan({
        ...selectedLoan,
        orders: selectedLoan.orders.map(order => ({ ...order })),
      });
    }
    handleCloseMenu();
  };

  const handleSaveDetails = () => {
    if (!editLoan) {
      return;
    }

    setLoans(currentLoans =>
      currentLoans.map(loan =>
        loan.loanId === editLoan.loanId ? editLoan : loan
      )
    );
    setEditLoan(null);
    setGeneratedLink('');
  };

  const handleOrderAmountChange = (
    orderId: string,
    amount: number | string
  ) => {
    setEditLoan(currentLoan =>
      currentLoan
        ? {
            ...currentLoan,
            orders: currentLoan.orders.map(order =>
              order.id === orderId
                ? { ...order, amount: Number(amount) || 0 }
                : order
            ),
          }
        : currentLoan
    );
  };

  const handleRushChange = (orderId: string, isRush: boolean) => {
    setEditLoan(currentLoan =>
      currentLoan
        ? {
            ...currentLoan,
            orders: currentLoan.orders.map(order =>
              order.id === orderId ? { ...order, isRush } : order
            ),
          }
        : currentLoan
    );
  };

  const handleGenerateLink = () => {
    if (!amcId || !loans.length) {
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmGenerateLink = () => {
    setConfirmOpen(false);
    setIsGenerating(true);
    setGeneratedLink('');
    service
      .generatePaymentLink(
        Number(amcId),
        loans.map(toPaymentLoanRequest)
      )
      .then(response => {
        const data: BulkAppraisalPaymentLinkResponse = response.data.data;
        setGeneratedLink(data.paymentLink || '');
        setValidationErrors([]);
      })
      .catch(() => {
        setValidationErrors([
          {
            loanId: 0,
            reason: 'Unable to generate payment link',
          },
        ]);
      })
      .finally(() => setIsGenerating(false));
  };

  const handleReset = () => {
    setLoans([]);
    setLoanInput('');
    setLoanOptions([]);
    setValidationErrors([]);
    setGeneratedLink('');
  };

  return (
    <Layout title="Appraisal Payments" maxWidth="md">
      <Paper className={classes.paper}>
        <Box className={classes.header}>
          <span className={classes.headerDot} />
          <Typography variant="subtitle1">Generate Appraisal Links</Typography>
        </Box>

        <Box className={classes.fieldRow}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel>AMC (Appraisal Management Company)</InputLabel>
            <Select
              label="AMC (Appraisal Management Company)"
              value={amcId}
              onChange={event => {
                setAmcId(Number(event.target.value));
                handleReset();
              }}
            >
              {amcs.map(amc => (
                <MenuItem key={amc.amcId} value={amc.amcId}>
                  {amc.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box className={classes.loanInputHeader}>
          <Typography className={classes.loanInputLabel}>Loan IDs</Typography>
          <Typography className={classes.inputHint}>
            Type at least 3 characters. Press Enter or paste IDs to validate.
          </Typography>
        </Box>
        <Box className={classes.loanEntryRow}>
          <Autocomplete
            className={classes.loanInputField}
            disabled={!amcId}
            filterOptions={options => options}
            getOptionLabel={option => String(option.loanId)}
            getOptionSelected={(option, value) => option.loanId === value.loanId}
            inputValue={loanInput}
            loading={lookupLoading}
            multiple
            onChange={(_, value) => {
              setLoans(value as AppraisalPaymentLoan[]);
              setLoanInput('');
              setValidationErrors([]);
              setGeneratedLink('');
            }}
            onInputChange={(_, value, reason) => {
              if (reason !== 'reset') {
                setLoanInput(value);
              }
            }}
            options={loanOptions.filter(
              option => !loans.some(selected => selected.loanId === option.loanId)
            )}
            renderInput={params => (
              <TextField
                {...params}
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {lookupLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ',') {
                    event.preventDefault();
                    handleAddLoan();
                  }
                }}
                placeholder="Type or paste loan IDs..."
                variant="outlined"
              />
            )}
            renderOption={option => (
              <Box className={classes.optionText}>
                <Typography>{option.loanId} - {option.dealName || option.propertyAddress}</Typography>
                <Typography className={classes.optionMeta}>
                  {option.valuationCount} properties · {formatCurrency(Number(option.appraisalCost))}
                </Typography>
              </Box>
            )}
            renderTags={() =>
              loans.map(loan => (
                <Chip
                  className={classes.loanChip}
                  key={loan.loanId}
                  label={loan.loanId}
                  onDelete={() => handleRemoveLoan(loan.loanId)}
                />
              ))
            }
            value={loans}
          />
          <Button
            className={classes.addLoanButton}
            color="primary"
            disabled={!amcId || !loanInput.trim()}
            onClick={handleAddLoan}
            testId="add-loan-id-button"
            variant="contained"
          >
            Add loan ID
          </Button>
        </Box>

        {validationErrors.length > 0 && (
          <Box className={classes.validationErrors}>
            {validationErrors.map(error => (
              <Typography key={`${error.loanId}-${error.reason}`} variant="caption">
                {error.loanId ? `${error.loanId}: ` : ''}
                {error.reason}
              </Typography>
            ))}
          </Box>
        )}

        <Box className={classes.tableWrap}>
          <Table>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell className={classes.tableHeaderCell}>Loan ID</TableCell>
                <TableCell className={classes.tableHeaderCell} align="right">
                  Properties
                </TableCell>
                <TableCell className={classes.tableHeaderCell} align="right">
                  Total
                </TableCell>
                <TableCell className={classes.tableHeaderCell} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loans.map(loan => (
                <TableRow key={loan.loanId}>
                  <TableCell className={classes.loanIdCell}>{loan.loanId}</TableCell>
                  <TableCell align="right">{loan.valuationCount}</TableCell>
                  <TableCell className={classes.amountCell} align="right">
                    {formatCurrency(getLoanTotal(loan))}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton onClick={event => handleOpenMenu(event, loan.loanId)}>
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className={classes.totalRow}>
                <TableCell colSpan={2}>Total</TableCell>
                <TableCell className={classes.amountCell} align="right">
                  {formatCurrency(total)}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        <Box className={classes.actions}>
          <Button
            onClick={handleReset}
            testId="reset-appraisal-payments-button"
            variant="outlined"
          >
            Reset
          </Button>
          <Button
            color="primary"
            disabled={!loans.length || isGenerating}
            onClick={handleGenerateLink}
            testId="generate-appraisal-payment-link-button"
            variant="contained"
          >
            {isGenerating ? 'Generating...' : 'Generate payment link'}
          </Button>
        </Box>

        {generatedLink && (
          <Box className={classes.linkPanel}>
            <Typography className={classes.linkTitle}>
              Payment link generated
            </Typography>
            <Box className={classes.linkContent}>
              <Box className={classes.linkValue}>{generatedLink}</Box>
              <Button
                onClick={() => navigator.clipboard?.writeText(generatedLink)}
                testId="copy-appraisal-payment-link-button"
                variant="outlined"
              >
                Copy link
              </Button>
            </Box>
          </Box>
        )}

        <Menu
          anchorEl={menuAnchor}
          keepMounted
          onClose={handleCloseMenu}
          open={Boolean(menuAnchor)}
        >
          <MenuItem onClick={handleEditDetails}>Edit details</MenuItem>
          <MenuItem onClick={() => handleRemoveLoan(selectedLoanId || undefined)}>
            Remove loan
          </MenuItem>
        </Menu>

        <Dialog
          className={classes.dialogPaper}
          maxWidth="md"
          onClose={() => setEditLoan(null)}
          open={Boolean(editLoan)}
        >
          <DialogTitle className={classes.dialogTitle} disableTypography>
            <Box className={classes.dialogTitleRow}>
              <Typography variant="h3">Edit Details</Typography>
              <IconButton onClick={() => setEditLoan(null)}>
                <Close />
              </IconButton>
            </Box>
            <Typography>
              Enter the amount for each order. Rush adds {formatCurrency(RUSH_FEE)} per order.
            </Typography>
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <Box className={classes.orderList}>
              {editLoan?.orders.map(order => (
                <Box
                  className={`${classes.orderRow} ${
                    order.isRush ? classes.rushRow : ''
                  }`}
                  key={order.id}
                >
                  <Typography className={classes.address}>{order.address}</Typography>
                  <CurrencyField
                    name={order.id}
                    onChange={(_, value) => handleOrderAmountChange(order.id, value)}
                    testId={`appraisal-payment-amount-${order.id}`}
                    value={order.amount}
                    variant="outlined"
                  />
                  <Box className={classes.rushCell}>
                    <Checkbox
                      checked={order.isRush}
                      color="primary"
                      onChange={event =>
                        handleRushChange(order.id, event.target.checked)
                      }
                    />
                    <Box>
                      <Typography className={classes.rushLabel}>Rush</Typography>
                      <Typography className={classes.rushFee}>
                        +{formatCurrency(RUSH_FEE)}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography className={classes.lineTotal}>
                    {formatCurrency(getOrderTotal(order))}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box className={classes.totalsPanel}>
              <Box className={classes.totalLine}>
                <span>Subtotal ({editLoan?.orders.length || 0} orders)</span>
                <span>{formatCurrency(editSubtotal)}</span>
              </Box>
              <Box className={classes.totalLine}>
                <span>
                  Rush fees ({editRushCount} x {formatCurrency(RUSH_FEE)})
                </span>
                <span>{formatCurrency(editRushFees)}</span>
              </Box>
              <Box className={`${classes.totalLine} ${classes.finalTotalLine}`}>
                <span>Total Payment</span>
                <span>{formatCurrency(editTotal)}</span>
              </Box>
            </Box>

            <Box className={classes.dialogActions}>
              <Button
                onClick={() => setEditLoan(null)}
                testId="cancel-appraisal-payment-details-button"
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                color="primary"
                onClick={handleSaveDetails}
                testId="save-appraisal-payment-details-button"
                variant="contained"
              >
                Save details
              </Button>
            </Box>
          </DialogContent>
        </Dialog>

        <Dialog
          className={classes.confirmDialogPaper}
          maxWidth="xs"
          onClose={() => setConfirmOpen(false)}
          open={confirmOpen}
        >
          <DialogTitle className={classes.confirmDialogTitle} disableTypography>
            <Box className={classes.dialogTitleRow}>
              <Typography variant="subtitle1">Confirm payment link</Typography>
              <IconButton size="small" onClick={() => setConfirmOpen(false)}>
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent className={classes.confirmDialogContent}>
            <Typography className={classes.confirmDialogText}>
              You are about to generate a payment link for {loans.length}{' '}
              {loans.length === 1 ? 'loan' : 'loans'} totaling{' '}
              <strong>{formatCurrency(total)}</strong>. Do you want to continue?
            </Typography>

            <Box className={classes.dialogActions}>
              <Button
                onClick={() => setConfirmOpen(false)}
                testId="cancel-generate-payment-link-button"
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                color="primary"
                disabled={isGenerating}
                onClick={handleConfirmGenerateLink}
                testId="confirm-generate-payment-link-button"
                variant="contained"
              >
                {isGenerating ? 'Generating...' : 'Confirm'}
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      </Paper>
    </Layout>
  );
};
